import React, { useEffect, useRef, useState } from "react";
import cx from "./cx";
import RevButton from "./RevButton";

const WIDTH_CLASS = { 520: 'rev-modal__panel--w520', 420: 'rev-modal__panel--w420' };
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const OPEN_PANELS = [];

let titleSeq = 0;

/**
 * Modal del DS: fondo, panel, cierre por clic fuera, Escape y botón de cerrar.
 *  - open: false mantiene el nodo montado pero oculto (.rev-modal--closed) e inerte
 *    (sin listeners ni trampa de foco)
 *  - onClose: clic en el fondo, clic en la X o Escape (recibe el evento)
 *  - closable: false quita la X y desactiva el cierre por fondo y por Escape
 *  - width: 520 | 420 (por defecto el panel ancho de 920)
 *  - closeLabel: aria-label de la X y del panel cuando no hay título
 *  - title: título en versalitas (h3) bajo la X, referenciado por aria-labelledby
 *  - footer: franja inferior a sangre (.rev-modal__footer)
 *
 * A11y: el panel es role="dialog" aria-modal, recibe el foco al abrirse, atrapa el
 * Tab mientras está abierto y devuelve el foco al disparador al cerrarse. Con varios
 * modales apilados solo el de encima responde a Escape y atrapa el Tab; los de debajo
 * quedan intactos hasta que vuelven a ser el último de la pila.
 */
const RevModal = ({ open = true, onClose, closable = true, width, closeLabel, title, footer, className, children, ...rest }) => {
    const panelRef = useRef(null);
    const triggerRef = useRef(null);
    const latest = useRef({ onClose, closable });
    const [titleId] = useState(() => {
        titleSeq += 1;
        return `ds-modal-title-${titleSeq}`;
    });
    latest.current = { onClose, closable };

    useEffect(() => {
        if (!open) return undefined;
        const panel = panelRef.current;
        triggerRef.current = document.activeElement;
        if (panel) {
            OPEN_PANELS.push(panel);
            panel.focus();
        }
        const handleKeyDown = (e) => {
            if (!panel || OPEN_PANELS[OPEN_PANELS.length - 1] !== panel) return;
            if (e.key === 'Escape') {
                if (!latest.current.closable) return;
                if (latest.current.onClose) latest.current.onClose(e);
                return;
            }
            if (e.key !== 'Tab') return;
            const items = Array.from(panel.querySelectorAll(FOCUSABLE))
                .filter((el) => !el.disabled && el.offsetParent !== null);
            if (items.length === 0) {
                e.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            if (!panel.contains(active)) {
                e.preventDefault();
                (e.shiftKey ? last : first).focus();
            } else if (e.shiftKey && (active === first || active === panel)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            const stacked = OPEN_PANELS.lastIndexOf(panel);
            if (stacked !== -1) OPEN_PANELS.splice(stacked, 1);
            const trigger = triggerRef.current;
            if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) trigger.focus();
        };
    }, [open]);

    const dismiss = closable ? onClose : undefined;

    return (
        <div
            className={cx('rev-modal', !open && 'rev-modal--closed', className)}
            onClick={dismiss}
            aria-hidden={open ? undefined : true}
            {...rest}
        >
            <div
                ref={panelRef}
                className={cx('rev-modal__panel', WIDTH_CLASS[width])}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-label={!title ? closeLabel : undefined}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
            >
                {closable && (
                    <RevButton variant="secondary" icon className="rev-modal__close" onClick={onClose} aria-label={closeLabel}>
                        <span className="material-icons rev-icon-sm">close</span>
                    </RevButton>
                )}
                {title && <h3 id={titleId} className="rev-card__title">{title}</h3>}
                {children}
                {footer && <div className="rev-modal__footer">{footer}</div>}
            </div>
        </div>
    );
};

export default RevModal;
